<template>
  <div v-if="development" class="page-development-view">
    <SectionsDevelopmentBanner :development="development as Development" />
    <div class="container">
      <div class="row">
        <div class="col-lg-8">
          <div class="row">
            <div class="col-md-12">
              <!-- Description -->
              <BlocksCard class="description-block" :class="{'--showing-more': isViewMore}">
                <h2 class="__heading">
                  Description
                </h2>
                <div class="description-block__inner" :style="!isViewMore ? `max-height: 360px;` : ''">
                  <p v-html="formatTextAreaContent(development.description || '')" />
                  <div id="fadeOverlay" class="fade" />
                </div>
                <div class="__view-more-btn" @click="isViewMore = !isViewMore">
                  See {{ isViewMore ? 'Less' : 'More' }}
                  <img v-lazy-load  :data-src="chevron" alt="Down arrow icon">
                </div>
              </BlocksCard>

              <!-- Builder logos -->
              <BlocksCard v-if="development.companies?.length">
                <h2 class="__heading">
                  Builders
                </h2>
                <div class="row __builders-logo-list">
                  <div v-for="(company, key) in development.companies" :key="`builder-logo-${key}`" class="col-6 col-md-4 col-lg-3 __single-logo">
                    <BuilderWidget widget-type="Logo" :builder="company" :analytics-event-data="{displayedLocation: `${development.slug} development page`, position: (key + 1) as number}" />
                  </div>
                </div>
              </BlocksCard>

              <!-- Gallery -->
              <BlocksGallery v-if="development.gallery.data.length > 0" heading="Gallery" :alt-text="`${development.name} gallery image`" :images="development.gallery.data" click-to-view-full-screen :analytics-event-data="{entity: 'Development', imageType: 'gallery', slug: development.slug, displayedLocation: 'development view'}" />

              <!-- Stages -->
              <BlocksGallery v-if="development.stages.data.length > 0" heading="Stages" :images="development.stages.data" :alt-text="`${development.name} stages image`" click-to-view-full-screen :analytics-event-data="{entity: 'Development', imageType: 'stages', slug: development.slug, displayedLocation: 'development view'}" />

              <!-- Documents View -->
              <CommonDocumentsViewer v-if="development.documents.data.length > 0" heading="Documents" :documents="development.documents.data" :analytics-event-data="{entity: 'Development', slug: development.slug, displayedLocation: 'development view'}" />

              <!-- Location -->
              <client-only>
                <CommonLocationBlock :listing-or-development="development" />
              </client-only>
            </div>
          </div>
        </div>
        <div class="col-lg-4">
          <!-- Developer -->
          <DevelopmentDeveloper :development="development" />

          <!-- Key Info -->
          <DevelopmentKeyInfo :development="development" />

          <!-- Commute Times -->
          <CommonCommuteTimes v-if="development && development.commuteTimes.data.length" :commute-time-data-results="development.commuteTimes.data" />

          <!-- Related listings -->
          <BlocksListingsRelated :key="`related-listing-desktop`" :listing-types="['Design']" class="desktop-only" />
        </div>
      </div>
    </div>

    <SectionsListingCategorized :heading="`Current listings in ${development.name}`" :custom-listing-params="{filterBy: `development.slug:=${development.slug}`, perPage: 12}" sub-heading="Top Picks" :view-more-link="`/builder/${development.slug}/listings`" :show-category-filters="true" :hidden-filters="['developments', 'builders', 'designs']" :hide-view-more-button="true" />

    <!-- Related Listings -->
    <client-only>
      <BlocksListingsRelated :key="`related-listing-mobile`" :listing-types="['Design']" class="mobile-only" style="margin-top: 28px" />
    </client-only>

    <LazyAdAdBar ad-display-location="development_page_banner" />
  </div>
</template>

<script setup lang="ts">
import {Ref} from 'vue';
import {useFetch, useRoute} from '#app';
import globalApiHeaders from '~/composables/globalApiHeaders';
import {ApiReturn} from '~/@types/common';
import {Development} from '~/@types/development';
import chevron from 'assets/images/icons/chevron-down.webp';

// DATA Definitions
//----------------------------------------------------------------------------------------------------------------------
const route = useRoute();
const config = useRuntimeConfig();
const development: Ref<Development | null> = ref(null);
const {$analyticsTrack, $metaPixelTrackEvent} = useNuxtApp();
const isViewMore: Ref<boolean> = ref(false);

// DATA Fetching
//----------------------------------------------------------------------------------------------------------------------
const {data} = await useFetch(() => `/public/developments/show/${route.params.slug}`,
  { baseURL: config.public.apiUrl, headers: globalApiHeaders()},
);
if (data.value) {
  if ((data.value as ApiReturn).code === 200 && (data.value as ApiReturn).data) {
    // Data is returned
    development.value = (data.value as ApiReturn).data as Development;
  } else if ((data.value as ApiReturn).code === 404) {
    // No data is returned. Page not found
    throw createError({statusCode: 404, message: 'Development not found', fatal: true});
  } else {
    // Something went wrong
    throw createError({statusCode: 500, message: 'Something went wrong', fatal: true});
  }
}

/**
 * @_HEAD Hook
 */
useHead({
  meta: [
    {
      name: 'canonical',
      content: 'https://www.newhomes.co.nz' + route.fullPath,
    },
  ],
});

const seoMetaData = getSeoMeta();
useSeoMeta({
  title: seoMetaData.title,
  description: seoMetaData.description,
  ogTitle: seoMetaData.title,
  ogDescription: seoMetaData.description,
  ogImage: seoMetaData.imageUrl,
  twitterCard: 'summary_large_image',
  twitterTitle: seoMetaData.title,
  twitterDescription: seoMetaData.description,
  twitterImage: seoMetaData.imageUrl,
});

function getSeoMeta (): {title: string, description: string,imageUrl: string} {
  const developmentName = development.value ? development.value.name : 'Development';
  const seoImageUrl = development.value?.heroImageUrl ?? 'https://store.newhomeshub.co.nz/static-assets/new-homes-og-image-v2.jpg';
  if (development.value && development.value.seo) {
    const { title, description, imageUrl } = development.value.seo;
    return {
      title: title || `${developmentName} | New Homes - Explore ${developmentName} in Detail`,
      description: description || `Discover ${developmentName} on New Homes. Get detailed info about development stages, listings, map, and contact details.`,
      imageUrl: imageUrl || seoImageUrl,
    };
  } else {
    return {
      title: `${developmentName} | New Homes - Explore ${developmentName} in Detail`,
      description: `Discover ${developmentName} on New Homes. Get detailed info about development stages, listings, map, and contact details.`,
      imageUrl: seoImageUrl,
    };
  }
}


/**
 * @_MOUNTED Hook
 */
onMounted(() => {
  triggerAnalyticsTracking();
});

/**
 * Trigger analytics tracking
 */
function triggerAnalyticsTracking () {
  if (!development.value) return;

  // Track analytics event
  $analyticsTrack({
    action: 'view',
    entity: 'Development',
    property: 'development',
    entitySlug: development.value.slug,
    displayedLocation: 'development page',
    eventData: {
      userIntention: 'active',
      component: 'development page',
    },
  });

  // Track meta event
  $metaPixelTrackEvent('ViewContent', {
    content_name: development.value.name,
    content_category: development.value?.type ?? 'Development',
    content_ids: [development.value.did],
    content_type: 'Development',
  });
}

// SEO Metadata
//----------------------------------------------------------------------------------------------------------------------
if (development.value && development.value.seo) {
  const { title, description, imageUrl, robots } = development.value.seo;
  useSeoMeta({
    title: title || development.value.name,
    description: description,
    robots: robots || 'index, follow',
    ogTitle: title || development.value.name,
    ogDescription: description,
    ogImage: imageUrl || development.value?.heroImageUrl,
    ogType: 'website',
    twitterCard: 'summary_large_image',
    twitterTitle: title || development.value.name,
    twitterDescription: description,
    twitterImage: imageUrl || development.value?.heroImageUrl,
  });
}
</script>

<style lang="scss">
.page-development-view {
  .__builders-logo-list {
    .__single-logo {
      display: flex;
      align-items: center;
      justify-content: center;

      img {
        padding: 5px;
        max-width: 100px;
        max-height: 60px;
        margin-bottom: 50px;
      }
    }
  }

  .__development-wrapper {
    margin-top: 12px;
    margin-bottom: 12px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;

    .__development-logo-wrapper {
      width: 185px;
      height: 185px;
      border-radius: 50%;
      overflow: hidden;
      background: #fff;
      border: 1px solid #e0e0e0;
      padding: 12px;
      display: flex;
      justify-content: center;
      align-items: center;

      img {
        max-width: 100%;
        max-height: 100%;
      }
    }
  }

  .__developer-name {
    text-align: center;
    margin-bottom: 24px;
  }


  .description-block {

    &__inner {
      max-height: fit-content;
      overflow-y: hidden;
      position: relative;
      margin-bottom: 20px;

      .fade {
        position: absolute;
        bottom: 0;
        left: 0;
        width: 100%;
        height: 80px; /* Increased height */
        background-image: linear-gradient(to bottom, rgba(255,255,255,0), white 90%);
      }
    }

    .__view-more-btn {
      width: 100%;
      text-align: center;
      font-size: 14px;
      color: rgb(var(--text-dark));
      cursor: pointer;
      transform: scale(1);
      transition: all 0.2s ease-in-out;

      img {
        width: 15px;
        margin-left: 4px;
        transition: all 0.2s ease-in-out;
      }

      &:hover {
        transform: scale(1.05);
        transition: all 0.2s ease-in-out;
      }
    }

    &.--showing-more {
      .description-block__inner {
        .fade {
          height: 0;
        }
      }

      .__view-more-btn {
        img {
          transform: rotate(180deg) !important;
          transition: all 0.2s ease-in-out;
        }
      }
    }
  }
}
</style>
